/**
 * Learning Bite Viewer Component
 * Complete, self-contained viewer for GiftFeud learning bites
 * Handles rendering, progress tracking, and all user interactions
 */

import React, { useState, useEffect, useRef } from 'react';
import { GiftFeudSDK } from '../core/GiftFeudSDK';
import { LearningBite, Slide } from '../types';
import { Button } from './ui/Button';
import { Textarea } from './ui/Textarea';
import { 
  X, ChevronLeft, ChevronRight, Clock, Check, Star
} from 'lucide-react';

export interface LearningBiteViewerProps {
  /** GiftFeudSDK instance */
  sdk: GiftFeudSDK;
  
  /** Share token for the learning bite */
  shareToken: string;
  
  /** Optional PIN for protected learning bites */
  pin?: string;
  
  /** Campaign ID for progress tracking */
  campaignId?: string;
  
  /** Callback when learning bite is completed */
  onComplete?: (data: { timeSpent: number; answers: Record<string, any> }) => void;
  
  /** Callback when viewer is closed */
  onClose?: () => void;
  
  /** Display mode: fullscreen or modal */
  mode?: 'fullscreen' | 'modal';
  
  /** Optional theme configuration */
  theme?: {
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    backgroundImageUrl?: string;
    fontColor?: string;
    headingFont?: string;
    bodyFont?: string;
    cardOpacity?: number; // Card overlay opacity (0-1) when using background images
  };
  
  /** Show bonus rolls earned message */
  bonusRolls?: number;
}

// Separate component for audio content to properly use hooks
interface AudioContentProps {
  audioData: any;
  slideStyle: any;
  slide: Slide;
  sdk: GiftFeudSDK;
  learningBite: LearningBite | null;
  campaignId?: string;
}

const AudioContent: React.FC<AudioContentProps> = ({ 
  audioData, 
  slideStyle, 
  slide, 
  sdk, 
  learningBite, 
  campaignId 
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [maxPosition, setMaxPosition] = useState(0);
  const lastUpdateRef = useRef(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      const currentTime = audio.currentTime;
      const duration = audio.duration;

      if (currentTime > maxPosition) {
        setMaxPosition(currentTime);

        // Send update every 5 seconds
        const now = Date.now();
        if (now - lastUpdateRef.current > 5000 && learningBite) {
          lastUpdateRef.current = now;
          sdk?.updateMediaProgress(
            learningBite.id,
            slide.id,
            currentTime,
            duration,
            campaignId
          );
        }
      }
    };

    const handleEnded = () => {
      // Send final update when audio ends
      if (learningBite) {
        sdk?.updateMediaProgress(
          learningBite.id,
          slide.id,
          audio.duration,
          audio.duration,
          campaignId
        );
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      // Send final update when component unmounts
      if (maxPosition > 0 && learningBite) {
        sdk?.updateMediaProgress(
          learningBite.id,
          slide.id,
          maxPosition,
          audio.duration || maxPosition,
          campaignId
        );
      }
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [slide.id, maxPosition, sdk, learningBite, campaignId]);

  return (
    <div className="p-8 space-y-4 max-w-2xl mx-auto text-center">
      {audioData.title && (
        <h2 className="text-2xl font-bold" style={{ color: slideStyle.textColor }}>
          {audioData.title}
        </h2>
      )}
      {audioData.description && (
        <p style={{ color: slideStyle.textColor, opacity: 0.8 }}>
          {audioData.description}
        </p>
      )}
      <div className="flex justify-center items-center">
        <audio ref={audioRef} controls className="w-full max-w-md">
          <source src={audioData.url} type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      </div>
    </div>
  );
};

export const LearningBiteViewer: React.FC<LearningBiteViewerProps> = ({
  sdk,
  shareToken,
  pin,
  campaignId,
  onComplete,
  onClose,
  mode = 'fullscreen',
  theme,
  bonusRolls = 0,
}) => {
  // State
  const [learningBite, setLearningBite] = useState<LearningBite | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startTime] = useState(Date.now());
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  
  // Question-specific state
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [ratingValue, setRatingValue] = useState<number>(3);
  const [freeTextAnswer, setFreeTextAnswer] = useState<string>('');

  // Touch gestures
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const slideContainerRef = useRef<HTMLDivElement>(null);

  // Load learning bite on mount
  useEffect(() => {
    loadBite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  // Timer for time-limited bites
  useEffect(() => {
    if (learningBite?.timeLimitSeconds && !isCompleted) {
      const timer = setInterval(async () => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setTimeElapsed(elapsed);
        const remaining = Math.max(0, learningBite.timeLimitSeconds! - elapsed);
        setTimeRemaining(remaining);
        
        if (remaining === 0) {
          await handleComplete();
        }
      }, 1000);
      
      return () => clearInterval(timer);
    }
    
    return undefined;
  }, [learningBite, startTime, isCompleted]);

  const loadBite = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[LearningBiteViewer] Loading bite with shareToken:', shareToken);
      const bite = await sdk.loadLearningBite(shareToken, pin);
      console.log('[LearningBiteViewer] Bite loaded:', {
        id: bite.id,
        title: bite.title,
        slideCount: bite.slides?.length,
        slides: bite.slides?.map(s => ({ 
          type: s.type, 
          title: s.title,
          description: s.description,
          coverImage: s.coverImage,
          backgroundColor: s.backgroundColor,
          textColor: s.textColor,
          hasContent: !!s.contentData, 
          hasQuestion: !!s.question 
        }))
      });
      console.log('[LearningBiteViewer] Full slide data:', bite.slides);
      setLearningBite(bite);
      
      // Start progress tracking
      await sdk.startProgress(bite.id, campaignId);
      
      if (bite.timeLimitSeconds) {
        setTimeRemaining(bite.timeLimitSeconds);
      }
    } catch (err: any) {
      console.error('[LearningBiteViewer] Failed to load learning bite:', err);
      setError(err.message || 'Failed to load learning bite');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (!learningBite) return;
    
    const currentSlide = learningBite.slides[currentSlideIndex];
    const slideStartTime = Date.now();
    
    // Save answer for current slide if it's a question
    if (currentSlide.type === 'question') {
      const answer = saveAnswer(currentSlide);
      
      if (answer !== null) {
        await sdk.submitAnswer(
          learningBite.id,
          currentSlide.id,
          answer,
          campaignId
        );
      }
    }
    
    // Mark slide as completed
    const slideTimeSpent = Math.floor((Date.now() - slideStartTime) / 1000);
    await sdk.completeSlide(
      learningBite.id,
      currentSlide.id,
      slideTimeSpent,
      campaignId
    );
    
    if (currentSlideIndex < learningBite.slides.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
      resetQuestionState();
    } else {
      await handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
      resetQuestionState();
    }
  };

  const saveAnswer = (slide: Slide): any => {
    if (!slide.question) return null;
    
    let answer: any;
    switch (slide.question.type) {
      case 'RATING_SCALE':
      case 'NPS':
      case 'ENPS':
        answer = ratingValue;
        break;
      case 'MULTIPLE_CHOICE':
      case 'SURVEY':
        answer = selectedOptions;
        break;
      case 'TEXT_RESPONSE':
        answer = freeTextAnswer;
        break;
      case 'TRUE_FALSE':
        answer = freeTextAnswer === 'true'; // Convert string to boolean
        break;
      default:
        answer = null;
    }
    
    setAnswers(prev => ({ ...prev, [slide.id]: answer }));
    return answer;
  };

  const resetQuestionState = () => {
    setSelectedOptions([]);
    setRatingValue(3);
    setFreeTextAnswer('');
  };

  const handleComplete = async () => {
    if (!learningBite) return;
    
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    
    // Mark entire learning element as completed
    await sdk.complete(learningBite.id, timeSpent, campaignId);
    
    setIsCompleted(true);
    onComplete?.({ timeSpent, answers });
  };

  // Swipe gestures
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) handleNext();
    if (isRightSwipe) handlePrevious();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = learningBite ? ((currentSlideIndex + 1) / learningBite.slides.length) * 100 : 0;

  // Helper to get slide styling based on settings
  const getSlideStyle = (slide: Slide) => {
    const introSlide = learningBite?.slides.find(s => s.type === 'intro');
    
    let style = {
      backgroundColor: '#f9fafb',
      textColor: '#111827', // Dark gray for better readability
      backgroundImage: undefined as string | undefined,
    };
    
    // If useThemeColors is true, use campaign theme
    if (slide.useThemeColors && theme) {
      style = {
        backgroundColor: theme.backgroundColor || '#f9fafb',
        textColor: theme.fontColor || '#111827',
        backgroundImage: theme.backgroundImageUrl,
      };
    }
    // If useIntroBiteSettings is true and this isn't the intro, inherit from intro slide
    else if (slide.useIntroBiteSettings && slide.type !== 'intro' && introSlide) {
      style = {
        backgroundColor: introSlide.backgroundColor || '#f9fafb',
        textColor: introSlide.textColor || '#111827',
        backgroundImage: introSlide.backgroundImage,
      };
    }
    // Otherwise use slide's own settings
    else {
      style = {
        backgroundColor: slide.backgroundColor || '#f9fafb',
        textColor: slide.textColor || '#111827',
        backgroundImage: slide.backgroundImage,
      };
    }
    
    console.log('[LearningBiteViewer] Slide style:', {
      slideType: slide.type,
      slideTitle: slide.title,
      useThemeColors: slide.useThemeColors,
      useIntroBiteSettings: slide.useIntroBiteSettings,
      calculatedStyle: style
    });
    
    return style;
  };

  // Render functions
  const renderLoading = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
      <div 
        className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
        style={{ borderColor: theme?.secondaryColor || '#3b82f6' }}
      />
      <p style={{ color: theme?.fontColor }}>Loading learning bite...</p>
    </div>
  );

  const renderError = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 text-center p-8">
      <div className="text-5xl">⚠️</div>
      <h2 className="text-xl font-bold" style={{ color: theme?.fontColor }}>Oops!</h2>
      <p style={{ color: theme?.fontColor }}>{error}</p>
      <Button onClick={loadBite}>
        Try Again
      </Button>
    </div>
  );

  const renderComplete = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 text-center p-8">
      <div 
        className="w-20 h-20 rounded-full flex items-center justify-center text-4xl animate-bounce"
        style={{ backgroundColor: theme?.secondaryColor || '#10b981', color: 'white' }}
      >
        ✓
      </div>
      <h2 className="text-2xl font-bold" style={{ color: theme?.fontColor }}>
        Completed!
      </h2>
      <p style={{ color: theme?.fontColor }}>
        You've finished: <strong>{learningBite?.title}</strong>
      </p>
      {timeElapsed > 0 && (
        <div 
          className="px-4 py-2 rounded-lg"
          style={{ backgroundColor: `${theme?.primaryColor || '#3b82f6'}20` }}
        >
          <span 
            className="font-semibold"
            style={{ color: theme?.primaryColor || '#3b82f6' }}
          >
            Time taken: {formatTime(timeElapsed)}
          </span>
        </div>
      )}
      {bonusRolls > 0 && (
        <div 
          className="px-6 py-3 rounded-lg"
          style={{ backgroundColor: `${theme?.secondaryColor || '#10b981'}20` }}
        >
          <span 
            className="text-lg font-bold"
            style={{ color: theme?.secondaryColor || '#10b981' }}
          >
            +{bonusRolls} Bonus Rolls Earned! 🎲
          </span>
        </div>
      )}
      {onClose && (
        <Button 
          onClick={onClose}
          className="mt-4"
          style={{ backgroundColor: theme?.primaryColor }}
        >
          Continue
        </Button>
      )}
    </div>
  );

  // Content renderers
  const renderRatingScale = (slide: Slide, slideStyle: any) => {
    const question = slide.question;
    if (!question) return null;

    const scaleMax = question.maxValue || 5;
    const scaleMin = question.minValue || 1;
    
    // Use theme colors for answer buttons only if slide.useThemeColors is true
    const useThemeColors = slide.useThemeColors && theme;
    const buttonBgColor = useThemeColors ? theme.secondaryColor : '#f59e0b';
    const buttonTextColor = useThemeColors ? theme.fontColor : '#ffffff';
    const useCardOverlay = slide.useThemeColors && slideStyle.backgroundImage;

    return (
      <div className={useCardOverlay ? "space-y-6" : "space-y-6 p-8"}>
        <h2 className="text-2xl font-bold mb-4" style={{ color: slideStyle.textColor }}>
          {question.questionText || question.question}
        </h2>
        
        <div className="flex justify-center gap-3 flex-wrap">
          {Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => {
            const value = scaleMin + i;
            const isSelected = ratingValue === value;
            return (
              <button
                key={value}
                onClick={() => setRatingValue(value)}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all hover:shadow-lg"
                style={{ 
                  backgroundColor: useThemeColors ? buttonBgColor : (isSelected ? buttonBgColor : 'rgba(255, 255, 255, 0.95)'),
                  borderColor: useThemeColors ? buttonBgColor : (isSelected ? buttonBgColor : 'rgba(229, 231, 235, 0.5)'),
                  color: useThemeColors ? buttonTextColor : (isSelected ? '#ffffff' : '#1f2937'),
                  transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: isSelected ? '0 10px 20px rgba(0,0,0,0.15)' : '0 2px 4px rgba(0,0,0,0.1)',
                  opacity: useThemeColors ? 1 : (isSelected ? 1 : 0.95)
                }}
              >
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ 
                    backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.3)' : (useThemeColors ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6'),
                    color: useThemeColors ? buttonTextColor : (isSelected ? '#ffffff' : buttonBgColor)
                  }}
                >
                  <Star className="w-6 h-6" fill={isSelected ? 'currentColor' : 'none'} />
                </div>
                <span className="text-lg font-semibold">{value}</span>
              </button>
            );
          })}
        </div>

        {question.leftLabel && question.rightLabel && (
          <div className="flex justify-between text-sm" style={{ color: slideStyle.textColor, opacity: 0.7 }}>
            <span>{question.leftLabel}</span>
            <span>{question.rightLabel}</span>
          </div>
        )}
      </div>
    );
  };

  const renderMultipleChoice = (slide: Slide, slideStyle: any) => {
    const question = slide.question;
    if (!question || !question.options) return null;

    // Use theme colors for answer buttons only if slide.useThemeColors is true
    const useThemeColors = slide.useThemeColors && theme;
    const buttonBgColor = useThemeColors ? theme.secondaryColor : '#3b82f6';
    const buttonTextColor = useThemeColors ? theme.fontColor : '#ffffff';
    const useCardOverlay = slide.useThemeColors && slideStyle.backgroundImage;

    return (
      <div className={useCardOverlay ? "space-y-6" : "space-y-6 p-8"}>
        <h2 className="text-2xl font-bold mb-4" style={{ color: slideStyle.textColor }}>
          {question.questionText || question.question}
        </h2>
        
        <div className="space-y-3">
          {question.options.map((option) => {
            const isSelected = selectedOptions.includes(option.id);
            return (
              <button
                key={option.id}
                onClick={() => {
                  setSelectedOptions(prev => 
                    isSelected 
                      ? prev.filter(id => id !== option.id)
                      : [...prev, option.id]
                  );
                }}
                className="w-full p-4 rounded-lg border-2 text-left transition-all flex items-center gap-3 shadow-md hover:shadow-lg"
                style={{ 
                  backgroundColor: useThemeColors ? buttonBgColor : (isSelected ? buttonBgColor : 'rgba(255, 255, 255, 0.95)'),
                  borderColor: useThemeColors ? buttonBgColor : (isSelected ? buttonBgColor : 'rgba(229, 231, 235, 0.5)'),
                  color: useThemeColors ? buttonTextColor : (isSelected ? '#ffffff' : '#1f2937'),
                  opacity: useThemeColors ? 1 : (isSelected ? 1 : 0.95)
                }}
              >
                <div 
                  className="w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                  style={{
                    borderColor: useThemeColors ? buttonTextColor : (isSelected ? '#ffffff' : buttonBgColor),
                    backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.3)' : 'transparent'
                  }}
                >
                  {isSelected && <Check className="w-4 h-4" style={{ color: useThemeColors ? buttonTextColor : '#ffffff' }} />}
                </div>
                <span className="font-medium">{option.text}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTextResponse = (slide: Slide, slideStyle: any) => {
    const question = slide.question;
    if (!question) return null;
    
    const useThemeColors = slide.useThemeColors && theme;
    const useCardOverlay = slide.useThemeColors && slideStyle.backgroundImage;

    return (
      <div className={useCardOverlay ? "space-y-6" : "space-y-6 p-8"}>
        <h2 className="text-2xl font-bold mb-4" style={{ color: slideStyle.textColor }}>
          {question.questionText || question.question}
        </h2>
        
        <Textarea
          value={freeTextAnswer}
          onChange={(e) => setFreeTextAnswer(e.target.value)}
          placeholder="Type your answer here..."
          rows={6}
          className="w-full p-4 border-2 rounded-lg"
          style={{ 
            color: useThemeColors ? theme.fontColor : '#1f2937',
            backgroundColor: useThemeColors ? theme.secondaryColor : 'rgba(255, 255, 255, 0.95)',
            borderColor: useThemeColors ? theme.secondaryColor : 'rgba(229, 231, 235, 0.5)'
          }}
        />
      </div>
    );
  };

  const renderTrueFalse = (slide: Slide, slideStyle: any) => {
    const question = slide.question;
    if (!question) return null;

    // Use theme colors for answer buttons only if slide.useThemeColors is true
    const useThemeColors = slide.useThemeColors && theme;
    const buttonBgColor = useThemeColors ? theme.secondaryColor : '#10b981';
    const buttonTextColor = useThemeColors ? theme.fontColor : '#ffffff';
    const falseColor = '#dc2626'; // Keep false as red for semantic clarity (only when NOT using theme)
    const useCardOverlay = slide.useThemeColors && slideStyle.backgroundImage;

    return (
      <div className={useCardOverlay ? "space-y-6" : "space-y-6 p-8"}>
        <h2 className="text-2xl font-bold mb-6" style={{ color: slideStyle.textColor }}>
          {question.questionText || question.question}
        </h2>
        
        <div className="flex justify-center gap-4">
          <button
            onClick={() => {
              setSelectedOptions(['true']);
              setFreeTextAnswer('true');
            }}
            className="flex-1 max-w-xs p-6 rounded-xl border-2 transition-all hover:shadow-lg"
            style={{ 
              backgroundColor: useThemeColors ? buttonBgColor : (freeTextAnswer === 'true' ? buttonBgColor : 'rgba(255, 255, 255, 0.95)'),
              borderColor: useThemeColors ? buttonBgColor : (freeTextAnswer === 'true' ? buttonBgColor : 'rgba(229, 231, 235, 0.5)'),
              color: useThemeColors ? buttonTextColor : (freeTextAnswer === 'true' ? '#ffffff' : '#1f2937'),
              transform: freeTextAnswer === 'true' ? 'scale(1.05)' : 'scale(1)',
              boxShadow: freeTextAnswer === 'true' ? '0 10px 20px rgba(0,0,0,0.15)' : '0 2px 4px rgba(0,0,0,0.1)',
              opacity: useThemeColors ? 1 : (freeTextAnswer === 'true' ? 1 : 0.95)
            }}
          >
            <div className="flex flex-col items-center gap-3">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: freeTextAnswer === 'true' ? 'rgba(255, 255, 255, 0.3)' : (useThemeColors ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6'),
                  color: useThemeColors ? buttonTextColor : (freeTextAnswer === 'true' ? '#ffffff' : buttonBgColor)
                }}
              >
                <Check className="w-8 h-8" />
              </div>
              <span className="text-xl font-bold">
                Sandt
              </span>
            </div>
          </button>

          <button
            onClick={() => {
              setSelectedOptions(['false']);
              setFreeTextAnswer('false');
            }}
            className="flex-1 max-w-xs p-6 rounded-xl border-2 transition-all hover:shadow-lg"
            style={{ 
              backgroundColor: useThemeColors ? buttonBgColor : (freeTextAnswer === 'false' ? falseColor : 'rgba(255, 255, 255, 0.95)'),
              borderColor: useThemeColors ? buttonBgColor : (freeTextAnswer === 'false' ? falseColor : 'rgba(229, 231, 235, 0.5)'),
              color: useThemeColors ? buttonTextColor : (freeTextAnswer === 'false' ? '#ffffff' : '#1f2937'),
              transform: freeTextAnswer === 'false' ? 'scale(1.05)' : 'scale(1)',
              boxShadow: freeTextAnswer === 'false' ? '0 10px 20px rgba(0,0,0,0.15)' : '0 2px 4px rgba(0,0,0,0.1)',
              opacity: useThemeColors ? 1 : (freeTextAnswer === 'false' ? 1 : 0.95)
            }}
          >
            <div className="flex flex-col items-center gap-3">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: freeTextAnswer === 'false' ? 'rgba(255, 255, 255, 0.3)' : (useThemeColors ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6'),
                  color: useThemeColors ? buttonTextColor : (freeTextAnswer === 'false' ? '#ffffff' : falseColor)
                }}
              >
                <X className="w-8 h-8" />
              </div>
              <span className="text-xl font-bold">
                Falsk
              </span>
            </div>
          </button>
        </div>
      </div>
    );
  };

  const renderVideoContent = (slide: Slide, slideStyle: any) => {
    const videoData = slide.contentData;
    if (!videoData?.url) return null;

    // Extract video ID for YouTube/Vimeo
    const getEmbedUrl = (url: string): string | null => {
      const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([^"&?\/\s]{11})/);
      if (youtubeMatch) {
        return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
      }

      const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
      if (vimeoMatch) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
      }

      return url; // Direct video URL
    };

    const embedUrl = getEmbedUrl(videoData.url);
    const isDirectVideo = embedUrl === videoData.url && !videoData.url.includes('youtube') && !videoData.url.includes('vimeo');

    // For direct video URLs, use HTML5 video with tracking
    if (isDirectVideo) {
      const videoRef = React.useRef<HTMLVideoElement>(null);
      const [maxPosition, setMaxPosition] = React.useState(0);
      const lastUpdateRef = React.useRef(0);

      React.useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleTimeUpdate = () => {
          const currentTime = video.currentTime;
          const duration = video.duration;

          if (currentTime > maxPosition) {
            setMaxPosition(currentTime);

            // Send update every 5 seconds
            const now = Date.now();
            if (now - lastUpdateRef.current > 5000 && learningBite) {
              lastUpdateRef.current = now;
              sdk?.updateMediaProgress(
                learningBite.id,
                slide.id,
                currentTime,
                duration,
                campaignId
              );
            }
          }
        };

        const handleEnded = () => {
          // Send final update when video ends
          if (learningBite) {
            sdk?.updateMediaProgress(
              learningBite.id,
              slide.id,
              video.duration,
              video.duration,
              campaignId
            );
          }
        };

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('ended', handleEnded);

        return () => {
          // Send final update when component unmounts
          if (maxPosition > 0 && learningBite) {
            sdk?.updateMediaProgress(
              learningBite.id,
              slide.id,
              maxPosition,
              video.duration || maxPosition,
              campaignId
            );
          }
          video.removeEventListener('timeupdate', handleTimeUpdate);
          video.removeEventListener('ended', handleEnded);
        };
      }, [slide.id, maxPosition]);

      return (
        <div className="p-8 space-y-4">
          {videoData.title && (
            <h2 className="text-2xl font-bold" style={{ color: slideStyle.textColor }}>
              {videoData.title}
            </h2>
          )}
          {videoData.description && (
            <p style={{ color: slideStyle.textColor, opacity: 0.8 }}>
              {videoData.description}
            </p>
          )}
          <div className="w-full aspect-video rounded-lg overflow-hidden shadow-2xl">
            <video
              ref={videoRef}
              src={videoData.url}
              controls
              className="w-full h-full"
            >
              Your browser does not support the video element.
            </video>
          </div>
        </div>
      );
    }

    // For YouTube/Vimeo, use iframe (no tracking possible)
    return (
      <div className="p-8 space-y-4">
        {videoData.title && (
          <h2 className="text-2xl font-bold" style={{ color: slideStyle.textColor }}>
            {videoData.title}
          </h2>
        )}
        {videoData.description && (
          <p style={{ color: slideStyle.textColor, opacity: 0.8 }}>
            {videoData.description}
          </p>
        )}
        <div className="w-full aspect-video rounded-lg overflow-hidden shadow-2xl">
          <iframe
            src={embedUrl || ''}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  };

  const renderArticleContent = (slide: Slide, slideStyle: any) => {
    const articleData = slide.contentData;
    if (!articleData) return null;

    return (
      <div className="p-8 space-y-4 max-w-3xl mx-auto">
        {articleData.title && (
          <h2 className="text-3xl font-bold" style={{ color: slideStyle.textColor }}>
            {articleData.title}
          </h2>
        )}
        {articleData.content && (
          <div 
            className="prose prose-lg max-w-none"
            style={{ color: slideStyle.textColor }}
            dangerouslySetInnerHTML={{ __html: articleData.content }}
          />
        )}
      </div>
    );
  };

  const renderAudioContent = (slide: Slide, slideStyle: any) => {
    const audioData = slide.contentData;
    if (!audioData?.url) return null;

    return (
      <AudioContent 
        audioData={audioData} 
        slideStyle={slideStyle} 
        slide={slide}
        sdk={sdk}
        learningBite={learningBite}
        campaignId={campaignId}
      />
    );
  };

  const renderWebLinkContent = (slide: Slide, slideStyle: any) => {
    const linkData = slide.contentData;
    if (!linkData?.url) return null;

    return (
      <div className="p-8 space-y-4 max-w-2xl mx-auto text-center">
        {linkData.title && (
          <h2 className="text-2xl font-bold" style={{ color: slideStyle.textColor }}>
            {linkData.title}
          </h2>
        )}
        {linkData.description && (
          <p style={{ color: slideStyle.textColor, opacity: 0.8 }}>
            {linkData.description}
          </p>
        )}
        <Button
          onClick={() => window.open(linkData.url, '_blank')}
          style={{ backgroundColor: theme?.primaryColor }}
        >
          Visit Link →
        </Button>
      </div>
    );
  };

  // Main slide renderer
  const renderSlide = (slide: Slide) => {
    const slideStyle = getSlideStyle(slide);
    
    switch (slide.type) {
      case 'intro':
        const useIntroCardOverlay = slide.useThemeColors && slideStyle.backgroundImage;
        
        const introContainerStyle = {
          backgroundColor: useIntroCardOverlay ? undefined : slideStyle.backgroundColor,
          backgroundImage: slideStyle.backgroundImage ? `url(${slideStyle.backgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '400px',
        };
        
        const introCardStyle = useIntroCardOverlay ? {
          backgroundColor: theme?.primaryColor || '#ffffff',
          opacity: theme?.cardOpacity ?? 0.95,
          borderRadius: '1rem',
          padding: '2rem',
          margin: '2rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        } : undefined;
        
        const introContent = (
          <div className="flex flex-col items-center justify-center p-8 text-center"
               style={{ color: slideStyle.textColor }}>
            {slide.coverImage && (
              <img
                src={slide.coverImage}
                alt="Cover"
                className="max-w-xs max-h-48 object-contain mb-6 rounded-lg shadow-2xl"
              />
            )}
            {slide.title ? (
              <h1 className="text-4xl font-bold mb-4"
                  style={{ 
                    fontFamily: slide.useThemeColors ? theme?.headingFont : undefined,
                    color: slideStyle.textColor,
                  }}>
                {slide.title}
              </h1>
            ) : (
              <h1 className="text-4xl font-bold mb-4"
                  style={{ color: slideStyle.textColor, opacity: 0.5 }}>
                [No Title Set]
              </h1>
            )}
            {slide.description ? (
              <p className="text-xl max-w-2xl"
                 style={{ 
                   fontFamily: slide.useThemeColors ? theme?.bodyFont : undefined,
                   color: slideStyle.textColor,
                 }}>
                {slide.description}
              </p>
            ) : (
              <p className="text-xl max-w-2xl"
                 style={{ color: slideStyle.textColor, opacity: 0.5 }}>
                [No Description Set - Please add content in the admin system]
              </p>
            )}
          </div>
        );
        
        return (
          <div style={introContainerStyle} className="flex items-center justify-center min-h-[400px]">
            {useIntroCardOverlay ? (
              <div style={introCardStyle}>
                {introContent}
              </div>
            ) : (
              introContent
            )}
          </div>
        );

      case 'content':
        const useContentCardOverlay = slide.useThemeColors && slideStyle.backgroundImage;
        
        const containerStyle = {
          backgroundColor: useContentCardOverlay ? undefined : slideStyle.backgroundColor,
          backgroundImage: slideStyle.backgroundImage ? `url(${slideStyle.backgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '400px',
        };
        
        const contentCardStyle = useContentCardOverlay ? {
          backgroundColor: theme?.primaryColor || '#ffffff',
          opacity: theme?.cardOpacity ?? 0.95,
          borderRadius: '1rem',
          padding: '2rem',
          margin: '2rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        } : undefined;
        
        const renderContent = () => {
          if (slide.contentType === 'video') {
            return renderVideoContent(slide, slideStyle);
          } else if (slide.contentType === 'article') {
            return renderArticleContent(slide, slideStyle);
          } else if (slide.contentType === 'audio') {
            return renderAudioContent(slide, slideStyle);
          } else if (slide.contentType === 'web_link') {
            return renderWebLinkContent(slide, slideStyle);
          }
          return <p className="p-8">Unsupported content type: {slide.contentType}</p>;
        };
        
        return (
          <div style={containerStyle} className="flex items-center justify-center min-h-[400px]">
            {useContentCardOverlay ? (
              <div style={contentCardStyle}>
                {renderContent()}
              </div>
            ) : (
              renderContent()
            )}
          </div>
        );

      case 'question':
        const questionType = slide.question?.type;
        
        // Check if we should use card overlay (when using theme with background image)
        const useCardOverlay = slide.useThemeColors && slideStyle.backgroundImage;
        
        const questionContainerStyle = {
          backgroundColor: useCardOverlay ? undefined : slideStyle.backgroundColor,
          backgroundImage: useCardOverlay ? `url(${slideStyle.backgroundImage})` : (slideStyle.backgroundImage ? `url(${slideStyle.backgroundImage})` : undefined),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '400px',
        };
        
        const cardStyle = useCardOverlay ? {
          backgroundColor: theme?.primaryColor || '#ffffff',
          opacity: theme?.cardOpacity ?? 0.95,
          borderRadius: '1rem',
          padding: '2rem',
          margin: '2rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        } : undefined;
        
        const renderQuestion = () => {
          if (questionType === 'RATING_SCALE' || questionType === 'NPS' || questionType === 'ENPS') {
            return renderRatingScale(slide, slideStyle);
          } else if (questionType === 'MULTIPLE_CHOICE' || questionType === 'SURVEY') {
            return renderMultipleChoice(slide, slideStyle);
          } else if (questionType === 'TEXT_RESPONSE') {
            return renderTextResponse(slide, slideStyle);
          } else if (questionType === 'TRUE_FALSE') {
            return renderTrueFalse(slide, slideStyle);
          }
          return null;
        };
        
        return (
          <div style={questionContainerStyle} className="flex items-center justify-center min-h-[400px]">
            {useCardOverlay ? (
              <div style={cardStyle}>
                {renderQuestion()}
              </div>
            ) : (
              renderQuestion()
            )}
          </div>
        );
        
        return (
          <div style={questionContainerStyle}>
            <div className="p-8">
              <p style={{ color: slideStyle.textColor }}>Unsupported question type: {questionType}</p>
            </div>
          </div>
        );

      default:
        return (
          <div className="min-h-[400px] p-8 flex items-center justify-center">
            <p>Unknown slide type: {slide.type}</p>
          </div>
        );
    }
  };

  // Main render
  const content = loading ? renderLoading() : error ? renderError() : isCompleted ? renderComplete() : (
    <div className="flex flex-col h-full">
      {/* Header with Progress */}
      <div className="p-4 border-b bg-white">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium" style={{ color: theme?.fontColor }}>
            {currentSlideIndex + 1} / {learningBite?.slides.length}
          </span>
          <div className="flex items-center gap-4">
            {timeRemaining !== null && learningBite?.timeLimitSeconds && (
              <div 
                className={`flex items-center gap-1 text-sm font-medium`}
                style={{ color: timeRemaining < 60 ? '#dc2626' : theme?.fontColor }}
              >
                <Clock className="w-4 h-4" />
                <span>{formatTime(timeRemaining)}</span>
              </div>
            )}
            <span className="text-sm" style={{ color: theme?.fontColor, opacity: 0.7 }}>
              {Math.round(progress)}%
            </span>
            {onClose && (
              <button
                onClick={onClose}
                className="h-8 w-8 flex items-center justify-center rounded hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full transition-all duration-300 rounded-full"
            style={{ 
              width: `${progress}%`,
              backgroundColor: theme?.secondaryColor || '#10b981'
            }}
          />
        </div>
      </div>

      {/* Slide Content */}
      <div
        ref={slideContainerRef}
        className="flex-1 overflow-y-auto"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {learningBite && renderSlide(learningBite.slides[currentSlideIndex])}
      </div>

      {/* Navigation */}
      <div 
        className="p-4 border-t flex justify-between items-center gap-4"
        style={{ backgroundColor: theme?.backgroundColor || '#ffffff' }}
      >
        <Button
          onClick={handlePrevious}
          disabled={currentSlideIndex === 0}
          variant="outline"
          className="flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Previous</span>
        </Button>

        <div className="text-xs text-gray-400 sm:hidden">
          Swipe to navigate
        </div>

        <Button
          onClick={handleNext}
          className="flex items-center gap-2"
          style={{ backgroundColor: theme?.primaryColor }}
        >
          <span>{currentSlideIndex === (learningBite?.slides.length || 0) - 1 ? 'Finish' : 'Next'}</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // Render based on mode
  if (mode === 'fullscreen') {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-white">
        {content}
      </div>
    );
  }

  // Modal mode
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {content}
      </div>
    </div>
  );
};

